import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { nutritionAPI } from '../../services/api'
import {
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import NutritionDialog from '../shared/NutritionDialog'
import FavoriteCard from './FavoriteMealsModal/FavoriteCard'
import { contentLanguage } from '../../i18n/supported'

/**
 * FavoriteMealsModal Component
 *
 * Full-screen modal for managing favorite meals:
 * - Search by name
 * - Filter by category
 * - Sort by multiple fields
 * - Edit name, description, category, and meal type inline
 * - Drag-to-reorder (touch-friendly)
 * - Delete favorites
 * - Add to selected date
 * - Mobile-optimized layout with large touch targets
 */
export default function FavoriteMealsModal({
  isOpen,
  onClose,
  selectedDate,
  onMealAdded = () => {}
}) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const currentLanguage = contentLanguage(i18n?.language)

  // Determine the add-button label based on selectedDate vs today
  const getAddButtonLabel = () => {
    const now = new Date()
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    if (selectedDate === todayStr) {
      return t('favorites.addToToday', 'Add to today')
    }
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US'
    const dateObj = new Date(selectedDate + 'T12:00:00')
    const formattedDate = dateObj.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    return t('favorites.addToDate', { date: formattedDate, defaultValue: `Add to ${formattedDate}` })
  }
  const addButtonLabel = getAddButtonLabel()


  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [sortBy, setSortBy] = useState('default_meal_type')
  const [sortDesc, setSortDesc] = useState(false)

  // Edit state
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editMealType, setEditMealType] = useState('')
  const [editCategory, setEditCategory] = useState('')

  // Card expansion state — parent-owned so only one card is open at a time
  const [expandedFavoriteId, setExpandedFavoriteId] = useState(null)

  // Drag-to-reorder state
  const [draggedId, setDraggedId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)

  // Translation state
  const [translatedNames, setTranslatedNames] = useState({})

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchTimerRef = useRef(null)

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [searchQuery])

  // Fetch favorites
  const { data: favoritesData, isLoading } = useQuery({
    queryKey: ['favoriteMeals', 'all', sortBy, sortDesc, debouncedSearch, selectedCategory],
    queryFn: () => nutritionAPI.getFavoriteMeals({
      limit: 100,
      sort_by: sortBy,
      sort_desc: sortDesc,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(selectedCategory ? { category: selectedCategory } : {})
    }),
    select: (response) => response.data,
    enabled: isOpen
  })

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['favoriteMeals', 'categories'],
    queryFn: () => nutritionAPI.getFavoriteCategories(),
    select: (response) => response.data,
    enabled: isOpen
  })

  const categories = categoriesData || []

   
  const rawFavorites = favoritesData?.favorite_meals || []

  // Backend "default_meal_type" sort is alphabetical (breakfast / dinner /
  // lunch / snack). When this sort is active, override client-side to the
  // natural meal-time order (breakfast → lunch → dinner → snack), then by
  // name within each meal type.
  const MEAL_TYPE_ORDER = { breakfast: 0, lunch: 1, dinner: 2, snack: 3, other: 4 }

  const favorites = useMemo(() => {
    if (sortBy !== 'default_meal_type') return rawFavorites
    return [...rawFavorites].sort((a, b) => {
      const aOrder = MEAL_TYPE_ORDER[a.default_meal_type?.toLowerCase()] ?? 5
      const bOrder = MEAL_TYPE_ORDER[b.default_meal_type?.toLowerCase()] ?? 5
      if (aOrder !== bOrder) return aOrder - bOrder
      return (a.name || '').localeCompare(b.name || '')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawFavorites, sortBy])

  const favoriteIds = useMemo(() => favorites.map(f => f.id).join(','), [favorites])

  // Auto-translate
  useEffect(() => {
    const translateNames = async () => {
      if (currentLanguage === 'en' || favorites.length === 0) return
      const ids = favorites.map(f => f.id)
      const idsToTranslate = ids.filter(id => !translatedNames[id])
      if (idsToTranslate.length === 0) return
      try {
        const response = await nutritionAPI.translateFavoriteMealsBatch(idsToTranslate, currentLanguage)
        setTranslatedNames(prev => ({ ...prev, ...response.data.translations }))
      } catch (err) {
        console.error('Failed to translate favorite names:', err)
      }
    }
    translateNames()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteIds, currentLanguage])

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => nutritionAPI.updateFavoriteMeal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoriteMeals'] })
      setEditingId(null)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => nutritionAPI.deleteFavoriteMeal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoriteMeals'] })
    }
  })

  const addToTodayMutation = useMutation({
    mutationFn: (favoriteId) => nutritionAPI.addFavoriteToToday(favoriteId, null, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      onMealAdded()
    }
  })

  const reorderMutation = useMutation({
    mutationFn: (orderedIds) => nutritionAPI.reorderFavoriteMeals(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoriteMeals'] })
    }
  })

  // Display helpers
  const getDisplayName = (favorite) => {
    if (currentLanguage !== 'en' && translatedNames[favorite.id]) {
      return translatedNames[favorite.id].translated_name || favorite.name
    }
    return favorite.name
  }

  const mealTypes = {
    breakfast: { icon: '🌅', label: t('nutrition.mealTypes.breakfast', 'Breakfast') },
    lunch: { icon: '🌞', label: t('nutrition.mealTypes.lunch', 'Lunch') },
    dinner: { icon: '🌙', label: t('nutrition.mealTypes.dinner', 'Dinner') },
    snack: { icon: '🍎', label: t('nutrition.mealTypes.snack', 'Snack') },
    other: { icon: '🍽️', label: t('nutrition.mealTypes.other', 'Other') }
  }

  const getMealTypeDisplay = (mealType) => {
    return mealTypes[mealType?.toLowerCase()] || mealTypes.other
  }

  // Edit handlers
  const startEdit = (favorite) => {
    setEditingId(favorite.id)
    setEditName(favorite.name)
    setEditDescription(favorite.description || '')
    setEditMealType(favorite.default_meal_type?.toLowerCase() || 'other')
    setEditCategory(favorite.category || '')
  }

  const saveEdit = () => {
    if (!editName.trim()) return
    const data = {
      name: editName.trim(),
      description: editDescription.trim() || null,
      default_meal_type: editMealType,
      category: editCategory.trim() || null
    }
    updateMutation.mutate({ id: editingId, data })
  }

  const cancelEdit = () => {
    setEditingId(null)
  }

  const confirmDelete = (favorite) => {
    if (window.confirm(t('favorites.confirmDelete', `Delete "${favorite.name}"?`))) {
      deleteMutation.mutate(favorite.id)
    }
  }

  // Drag-to-reorder handlers
  const handleDragStart = useCallback((e, id) => {
    setDraggedId(id)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((e, id) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverId(id)
  }, [])

  const handleDragEnd = useCallback(() => {
    if (draggedId && dragOverId && draggedId !== dragOverId) {
      const currentIds = favorites.map(f => f.id)
      const fromIdx = currentIds.indexOf(draggedId)
      const toIdx = currentIds.indexOf(dragOverId)
      if (fromIdx !== -1 && toIdx !== -1) {
        const newOrder = [...currentIds]
        newOrder.splice(fromIdx, 1)
        newOrder.splice(toIdx, 0, draggedId)
        reorderMutation.mutate(newOrder)
      }
    }
    setDraggedId(null)
    setDragOverId(null)
  }, [draggedId, dragOverId, favorites, reorderMutation])

  // Touch-based reorder (long press)
  const touchStartRef = useRef(null)
  const handleTouchStart = useCallback((id) => {
    touchStartRef.current = { id, timer: setTimeout(() => setDraggedId(id), 500) }
  }, [])
  const handleTouchEnd = useCallback(() => {
    if (touchStartRef.current?.timer) clearTimeout(touchStartRef.current.timer)
    touchStartRef.current = null
  }, [])

  if (!isOpen) return null

  const isManualSort = sortBy === 'display_order'

  const handleToggleExpand = (favoriteId) => {
    setExpandedFavoriteId((current) => (current === favoriteId ? null : favoriteId))
  }

  const dialogTitle = (
    <span className="flex items-center gap-2">
      <StarIconSolid className="h-5 w-5 text-yellow-500" />
      {t('favorites.manageTitle', 'Manage Favorites')}
    </span>
  )

  return (
    <NutritionDialog open={isOpen} onClose={onClose} title={dialogTitle} size="lg">
      <div className="flex flex-col">
          {/* Toolbar (search/sort/categories) */}
          <div className="flex-shrink-0 -mx-4 -my-3 sm:mx-0 sm:my-0 px-4 py-3 sm:px-0 sm:py-0 sm:pb-3 border-b border-gray-200 sm:border-0 sm:border-b sm:mb-3 bg-surface-container">
            {/* Search bar */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('favorites.searchPlaceholder', 'Search favorites...')}
                className="w-full pl-10 pr-4 py-2.5 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category filter chips */}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    !selectedCategory
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('favorites.allCategories', 'All')}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      selectedCategory === cat
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Sort dropdown */}
            <div className="flex items-center justify-between">
              <select
                value={`${sortBy}_${sortDesc}`}
                onChange={(e) => {
                  const parts = e.target.value.split('_')
                  const desc = parts.pop()
                  const field = parts.join('_')
                  setSortBy(field)
                  setSortDesc(desc === 'true')
                }}
                className="text-sm px-3 py-2 text-gray-700"
              >
                <option value="last_used_at_true">{t('favorites.sort.recentlyUsed', 'Recently used')}</option>
                <option value="name_false">{t('favorites.sort.nameAZ', 'A-Z')}</option>
                <option value="name_true">{t('favorites.sort.nameZA', 'Z-A')}</option>
                <option value="times_used_true">{t('favorites.sort.mostUsed', 'Most used')}</option>
                <option value="created_at_true">{t('favorites.sort.newestFirst', 'Newest first')}</option>
                <option value="default_meal_type_false">{t('favorites.sort.mealType', 'Meal type')}</option>
                <option value="display_order_false">{t('favorites.sort.manual', 'Manual order')}</option>
              </select>
              <span className="text-sm text-gray-500">
                {favorites.length} {t('favorites.totalFavorites', 'favorite(s)')}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse h-24 bg-gray-100 rounded-lg" />
                ))}
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-12">
                <StarIconSolid className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t('favorites.empty', 'No favorites yet')}</p>
                {searchQuery || selectedCategory ? (
                  <p className="text-gray-400 mt-2">
                    {t('favorites.noResults', 'No matches — try a different search or category')}
                  </p>
                ) : (
                  <p className="text-gray-400 mt-2">
                    {t('favorites.emptyHint', 'Click the star on any meal to save it')}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {favorites.map((favorite) =>
                  editingId === favorite.id ? (
                    /* ---- Inline edit form ---- */
                    <div
                      key={favorite.id}
                      className="border border-gray-200 rounded-lg bg-surface-container p-4 space-y-3"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('favorites.name', 'Name')}
                        </label>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          maxLength={200}
                          className="w-full px-3 py-2.5 text-base"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('favorites.description', 'Description')}
                        </label>
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          maxLength={500}
                          rows={2}
                          placeholder={t('favorites.descriptionPlaceholder', 'Optional notes about this meal...')}
                          className="w-full px-3 py-2.5 text-base resize-none"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('nutrition.mealType', 'Meal Type')}
                          </label>
                          <select
                            value={editMealType}
                            onChange={(e) => setEditMealType(e.target.value)}
                            className="w-full px-3 py-2.5 text-base"
                          >
                            {Object.entries(mealTypes).map(([key, { icon, label }]) => (
                              <option key={key} value={key}>{icon} {label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('favorites.category', 'Category')}
                          </label>
                          <input
                            type="text"
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value)}
                            maxLength={100}
                            placeholder={t('favorites.categoryPlaceholder', 'e.g. Post-Workout')}
                            className="w-full px-3 py-2.5 text-base"
                            list="category-suggestions"
                          />
                          {categories.length > 0 && (
                            <datalist id="category-suggestions">
                              {categories.map(cat => <option key={cat} value={cat} />)}
                            </datalist>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2 pt-1">
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          {t('common.cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={saveEdit}
                          disabled={updateMutation.isPending || !editName.trim()}
                          className="px-4 py-2 text-sm text-on-neutral bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center"
                        >
                          <CheckIcon className="h-4 w-4 mr-1" />
                          {t('common.save', 'Save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <FavoriteCard
                      key={favorite.id}
                      favorite={favorite}
                      displayName={getDisplayName(favorite)}
                      expanded={expandedFavoriteId === favorite.id}
                      onToggleExpand={() => handleToggleExpand(favorite.id)}
                      onAdd={() => addToTodayMutation.mutate(favorite.id)}
                      onEdit={() => startEdit(favorite)}
                      onDelete={() => confirmDelete(favorite)}
                      isAdding={
                        addToTodayMutation.isPending &&
                        addToTodayMutation.variables === favorite.id
                      }
                      addLabel={addButtonLabel}
                      mealTypeIcon={getMealTypeDisplay(favorite.default_meal_type).icon}
                      isManualSort={isManualSort}
                      isDragging={draggedId === favorite.id}
                      isDragOver={dragOverId === favorite.id}
                      draggableProps={{
                        draggable: isManualSort,
                        onDragStart: (e) => handleDragStart(e, favorite.id),
                        onDragOver: (e) => handleDragOver(e, favorite.id),
                        onDragEnd: handleDragEnd,
                        onTouchStart: () =>
                          isManualSort && handleTouchStart(favorite.id),
                        onTouchEnd: handleTouchEnd,
                      }}
                    />
                  )
                )}
              </div>
            )}
          </div>
      </div>
    </NutritionDialog>
  )
}
